import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Search, Star, Mail, CalendarCheck, Building2, LayoutGrid, Check, Landmark, CreditCard, Languages, Loader2, Sparkles, Repeat, DollarSign, Upload, Image as ImageIcon, GripVertical, Gift, Clock, Inbox } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { EXPERTS, INVESTORS, CATEGORIES, SITE_SETTINGS, PAYMENT_METHODS, RESULTS_IMAGES } from "../../mock/mock";
import { translations } from "../../i18n/translations";
import { LANGUAGES } from "../../i18n/translations";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { api } from "../../lib/api";
import { mediaUrl } from "../../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadExperts = () => JSON.parse(localStorage.getItem("intro_experts") || "null") || EXPERTS;
const saveExperts = (e) => localStorage.setItem("intro_experts", JSON.stringify(e));

const emptyExpert = () => ({
  id: "e" + Date.now(), name: "", photo: "", category: "career", price: 150, rating: 5.0,
  reviews: 0, tier: "blue", featured: false, duration: 30, prices: { 15: "", 30: "", 60: "" },
  videoUrl: "", spokenLanguages: "", charity: "", plans: [], photos: [],
  title: { tr: "", en: "", ru: "", ar: "" }, bio: { tr: "", en: "", ru: "", ar: "" }, expertise: [],
});

const emptyPlan = () => ({
  id: "p" + Date.now(), period: "month", price: 1000, soldOut: false, popular: false,
  title: { tr: "", en: "", ru: "", ar: "" }, included: { tr: "", en: "", ru: "", ar: "" }, footnote: { tr: "", en: "", ru: "", ar: "" },
});

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-neutral-700">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const input = "w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-indigo-500 focus:bg-white transition text-sm";

// Reusable image uploader: file upload (Object Storage) + optional URL paste. Value is a URL string.
// Multi-image uploader: upload several photos (first = primary). Value is an array of URL strings.
const MultiImageUpload = ({ value = [], onChange, label }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const list = value || [];
  const add = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast({ title: "Lütfen bir görsel dosyası seçin" }); return; }
    setBusy(true);
    try { const r = await api.uploadFile(file); onChange([...list, r.url]); toast({ title: "Görsel yüklendi ✓" }); }
    catch { toast({ title: "Yükleme başarısız (yetki/dosya?)" }); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const addUrl = () => { const u = url.trim(); if (!u) return; onChange([...list, u]); setUrl(""); };
  const remove = (i) => onChange(list.filter((_, x) => x !== i));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= list.length) return; const a = [...list]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div>
      {label && <label className="text-sm font-medium text-neutral-700">{label}</label>}
      <div className="mt-1.5 flex flex-wrap gap-3">
        {list.map((u, i) => (
          <div key={i} className="relative w-24 h-24 rounded-2xl overflow-hidden border border-neutral-200 bg-neutral-100 group">
            <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute top-1 start-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white">Kapak</span>}
            <button type="button" onClick={() => remove(i)} className="absolute top-1 end-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center text-rose-500 shadow"><X className="w-3.5 h-3.5" /></button>
            <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 transition">
              <button type="button" onClick={() => move(i, -1)} className="w-6 h-6 rounded-full bg-white/90 text-neutral-700 text-xs shadow">‹</button>
              <button type="button" onClick={() => move(i, 1)} className="w-6 h-6 rounded-full bg-white/90 text-neutral-700 text-xs shadow">›</button>
            </div>
          </div>
        ))}
        <label className={`w-24 h-24 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-1 cursor-pointer text-neutral-400 ${busy ? "opacity-60" : "hover:border-indigo-400 hover:text-indigo-500"}`}>
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-[10px] font-semibold">{busy ? "..." : "Foto Ekle"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={add} disabled={busy} />
        </label>
      </div>
      <div className="mt-2 flex gap-2">
        <input className={input} placeholder="veya görsel URL yapıştır (opsiyonel)" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} />
        <Button type="button" variant="ghost" onClick={addUrl} className="rounded-xl shrink-0 border border-neutral-200">Ekle</Button>
      </div>
      <p className="mt-1.5 text-xs text-neutral-400">İlk fotoğraf kapak olarak kullanılır. Birden fazla eklersen uzman profilinde dönüşümlü gösterilir.</p>
    </div>
  );
};

const ImageUpload = ({ value, onChange, label, rounded = "rounded-2xl", aspect = "w-24 h-24" }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast({ title: "Lütfen bir görsel dosyası seçin" }); return; }
    setBusy(true);
    try {
      const res = await api.uploadFile(file);
      onChange(res.url);
      toast({ title: "Görsel yüklendi ✓" });
    } catch (err) {
      toast({ title: "Yükleme başarısız (yetki/dosya boyutu?)" });
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      {label && <label className="text-sm font-medium text-neutral-700">{label}</label>}
      <div className="mt-1.5 flex items-center gap-4">
        <div className={`${aspect} ${rounded} bg-neutral-100 border border-neutral-200 overflow-hidden flex items-center justify-center shrink-0`}>
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-neutral-300" />}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <label className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-semibold cursor-pointer border transition ${busy ? "opacity-60" : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-500"}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busy ? "Yükleniyor..." : "Bilgisayardan Yükle"}
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
          </label>
          <input className={input} placeholder="veya görsel URL yapıştır (opsiyonel)" value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </div>
      </div>
    </div>
  );
};

// Localized field with professional auto-translation (title / bio).
const LocalizedField = ({ label, value, onChange, multiline }) => {
  const [src, setSrc] = useState("tr");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const translate = async () => {
    const text = (value?.[src] || "").trim();
    if (!text) { setErr("Önce kaynak dilde metni gir."); return; }
    setErr(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/translate`, { text, source: src, targets: ["tr", "en", "ru", "ar"] });
      const tr = res.data?.translations || {};
      onChange({ ...value, ...tr });
    } catch (e) {
      setErr("Çeviri başarısız. Tekrar dene.");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-neutral-800">{label}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-400">Kaynak:</span>
          <select value={src} onChange={(e) => setSrc(e.target.value)} className="bg-neutral-50 border border-neutral-200 rounded-lg px-2 py-1 text-xs outline-none">
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.code.toUpperCase()}</option>)}
          </select>
          <button type="button" onClick={translate} disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />}
            {loading ? "Çevriliyor..." : "Otomatik Çevir"}
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {LANGUAGES.map((l) => (
          <div key={l.code} className="relative">
            <span className={`absolute top-2 ${l.dir === "rtl" ? "left-2" : "right-2"} text-[10px] font-bold px-1.5 py-0.5 rounded-full ${l.code === src ? "bg-indigo-100 text-indigo-600" : "bg-neutral-100 text-neutral-400"}`}>{l.flag} {l.code.toUpperCase()}</span>
            {multiline ? (
              <textarea rows={3} dir={l.dir} className={input} value={value?.[l.code] || ""} onChange={(e) => onChange({ ...value, [l.code]: e.target.value })} />
            ) : (
              <input dir={l.dir} className={input} value={value?.[l.code] || ""} onChange={(e) => onChange({ ...value, [l.code]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
      {err && <p className="mt-2 text-xs text-rose-500">{err}</p>}
      <p className="mt-2 text-xs text-neutral-400">Kaynak dili seç, metni gir ve "Otomatik Çevir" ile diğer dilleri profesyonelce doldur.</p>
    </div>
  );
};

const ExpertForm = ({ initial, onSave, onCancel }) => {
  const { t, loc } = useLang();
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLoc = (field, lng, v) => setF((p) => ({ ...p, [field]: { ...p[field], [lng]: v } }));
  const plans = f.plans || [];
  const [cats, setCats] = useState(CATEGORIES);
  useEffect(() => { api.getCategories().then((r) => { if (r?.length) setCats(r); }).catch(() => {}); }, []);
  const addPlan = () => set("plans", [...plans, emptyPlan()]);
  const updPlan = (idx, k, v) => set("plans", plans.map((p, i) => (i === idx ? { ...p, [k]: v } : p)));
  const delPlan = (idx) => set("plans", plans.filter((_, i) => i !== idx));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar shadow-2xl animate-scale-in">
        <div className="sticky top-0 bg-white/90 backdrop-blur border-b border-neutral-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">{initial.name ? t("admin.edit") : t("admin.add")}</h3>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Temel Bilgiler</p>
          <Field label="İsim"><input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <MultiImageUpload label="Fotoğraflar" value={(f.photos && f.photos.length) ? f.photos : (f.photo ? [f.photo] : [])} onChange={(arr) => { set("photos", arr); set("photo", arr[0] || ""); }} />
          <div className="grid sm:grid-cols-4 gap-4">
            <Field label="Kategori">
              <select className={input} value={f.category} onChange={(e) => set("category", e.target.value)}>
                {cats.map((c) => <option key={c.id} value={c.id}>{(c.name && loc(c.name)) || c.key || c.id}</option>)}
              </select>
            </Field>
            <Field label="Başlangıç ($)"><input type="number" className={input} value={f.price} onChange={(e) => set("price", +e.target.value)} /></Field>
            <Field label="Puan"><input type="number" step="0.1" className={input} value={f.rating} onChange={(e) => set("rating", +e.target.value)} /></Field>
            <Field label="Süre (dk)"><input type="number" className={input} value={f.duration} onChange={(e) => set("duration", +e.target.value)} /></Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Rozet">
              <select className={input} value={f.tier} onChange={(e) => set("tier", e.target.value)}>
                <option value="blue">Mavi</option><option value="gold">Altın</option>
              </select>
            </Field>
            <Field label="Değerlendirme"><input type="number" className={input} value={f.reviews} onChange={(e) => set("reviews", +e.target.value)} /></Field>
            <Field label="Öne Çıkan">
              <button onClick={() => set("featured", !f.featured)} className={`w-full rounded-xl px-3.5 py-2.5 text-sm font-medium border transition ${f.featured ? "bg-indigo-600 text-white border-indigo-600" : "bg-neutral-50 border-neutral-200"}`}>
                {f.featured ? "Evet ✓" : "Hayır"}
              </button>
            </Field>
          </div>
          <Field label="Uzmanlık (virgülle)">
            <input className={input} value={f.expertise.join(", ")} onChange={(e) => set("expertise", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} />
          </Field>

          <div className="pt-1">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-400 mb-2">Fiyatlandırma — Süreye Göre ($)</p>
            <div className="grid grid-cols-3 gap-3">
              {[15, 30, 60].map((dm) => (
                <Field key={dm} label={`${dm} dakika`}>
                  <input type="number" className={input} placeholder="otomatik"
                    value={(f.prices && f.prices[dm] != null) ? f.prices[dm] : ""}
                    onChange={(e) => set("prices", { ...(f.prices || {}), [dm]: e.target.value === "" ? "" : +e.target.value })} />
                </Field>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-neutral-400">Boş bırakılırsa başlangıç fiyatından otomatik hesaplanır (15dk %60, 30dk %100, 60dk %180).</p>
          </div>

          <div className="pt-2 space-y-4">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">İçerik & Diller</p>
            <LocalizedField label="Ünvan (dile göre)" value={f.title} onChange={(v) => set("title", v)} />
            <LocalizedField label="Biyografi (dile göre)" value={f.bio} onChange={(v) => set("bio", v)} multiline />
          </div>

          <div className="pt-2 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Ek Bilgiler</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Tanıtım Videosu (YouTube URL)"><input className={input} placeholder="https://youtube.com/..." value={f.videoUrl || ""} onChange={(e) => set("videoUrl", e.target.value)} /></Field>
              <Field label="Konuşulan Diller"><input className={input} placeholder="Türkçe, English" value={f.spokenLanguages || ""} onChange={(e) => set("spokenLanguages", e.target.value)} /></Field>
            </div>
            <Field label="Bağış Yapılan Kurum"><input className={input} placeholder="İyilik Vakfı" value={f.charity || ""} onChange={(e) => set("charity", e.target.value)} /></Field>
          </div>

          <div className="pt-2 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-neutral-400">Planlar / Paketler</p>
              <Button type="button" onClick={addPlan} className="rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm h-9">
                <Plus className="w-4 h-4 me-1" /> Plan Ekle
              </Button>
            </div>
            <p className="text-xs text-neutral-400 -mt-1">Aylık üyelik veya paket teklifleri. Uzman detay sayfasında "veya bir plan seç" bölümünde görünür. Boşsa sadece 1:1 danışmanlık gösterilir.</p>
            {plans.length === 0 && <p className="text-sm text-neutral-400 py-2">Henüz plan yok.</p>}
            {plans.map((pl, idx) => (
              <div key={pl.id} className="rounded-2xl border border-neutral-200 p-4 space-y-3 bg-neutral-50/50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Plan {idx + 1}</span>
                  <button type="button" onClick={() => delPlan(idx)} className="inline-flex items-center gap-1 text-sm text-rose-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /> Sil</button>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Fiyat ($)"><input type="number" className={input} value={pl.price} onChange={(e) => updPlan(idx, "price", +e.target.value)} /></Field>
                  <Field label="Periyot">
                    <select className={input} value={pl.period} onChange={(e) => updPlan(idx, "period", e.target.value)}>
                      <option value="month">Aylık (/ Ay)</option>
                      <option value="session">Görüşme (/ Görüşme)</option>
                    </select>
                  </Field>
                  <Field label="Durum">
                    <button type="button" onClick={() => updPlan(idx, "soldOut", !pl.soldOut)} className={`w-full rounded-xl px-3.5 py-2.5 text-sm font-medium border transition ${pl.soldOut ? "bg-neutral-800 text-white border-neutral-800" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>
                      {pl.soldOut ? "Tükendi" : "Aktif"}
                    </button>
                  </Field>
                </div>
                <button type="button" onClick={() => updPlan(idx, "popular", !pl.popular)} className={`w-full inline-flex items-center justify-center gap-1.5 rounded-xl px-3.5 py-2.5 text-sm font-semibold border transition ${pl.popular ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-neutral-200 text-neutral-600 hover:border-indigo-300"}`}>
                  <Sparkles className="w-4 h-4" /> {pl.popular ? "En Çok Tercih Edilen ✓" : "En Çok Tercih Edilen olarak işaretle"}
                </button>
                <LocalizedField label="Plan Başlığı (dile göre)" value={pl.title} onChange={(v) => updPlan(idx, "title", v)} />
                <LocalizedField label="Paket İçeriği — her satır bir madde (dile göre)" value={pl.included} onChange={(v) => updPlan(idx, "included", v)} multiline />
                <LocalizedField label="Alt Not (opsiyonel, dile göre)" value={pl.footnote} onChange={(v) => updPlan(idx, "footnote", v)} />
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-neutral-100 px-6 py-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} className="rounded-xl">{t("admin.cancel")}</Button>
          <Button onClick={() => onSave(f)} className="rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-6">{t("admin.save")}</Button>
        </div>
      </div>
    </div>
  );
};

export const AdminExperts = () => {
  const { t, lang, loc } = useLang();
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.getExperts().then((rows) => setList(rows.length ? rows : loadExperts())).catch(() => setList(loadExperts())); }, []);
  const filtered = list.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));

  const onSave = async (f) => {
    const exists = list.find((x) => x.id === f.id);
    try {
      if (exists) await api.updateExpert(f.id, f); else await api.createExpert(f);
      setList((p) => (exists ? p.map((x) => (x.id === f.id ? f : x)) : [f, ...p]));
      toast({ title: "Kaydedildi ✓" });
    } catch (e) { toast({ title: "Kayıt başarısız (yetki?)" }); }
    setEditing(null);
  };
  const remove = async (id) => {
    try { await api.deleteExpert(id); setList((p) => p.filter((x) => x.id !== id)); toast({ title: "Silindi" }); }
    catch (e) { toast({ title: "Silme başarısız" }); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white rounded-2xl border border-neutral-200 px-3.5 py-2.5 w-full sm:w-72">
          <Search className="w-4 h-4 text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Uzman ara..." className="flex-1 bg-transparent outline-none text-sm" />
        </div>
        <Button onClick={() => setEditing(emptyExpert())} className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold">
          <Plus className="w-4 h-4 me-1" /> {t("admin.add")}
        </Button>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
        <div className="divide-y divide-neutral-100">
          {filtered.map((e) => (
            <div key={e.id} className="flex items-center gap-4 p-4 hover:bg-neutral-50 transition-colors">
              <img src={e.photo} alt={e.name} className="w-12 h-12 rounded-2xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-neutral-900 truncate">{e.name}</span>
                  {e.tier === "gold" && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Gold</span>}
                  {e.featured && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">★</span>}
                </div>
                <p className="text-xs text-neutral-500 truncate">{loc(e.title)}</p>
              </div>
              <div className="hidden sm:flex items-center gap-1 text-sm text-neutral-600"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />{e.rating}</div>
              <div className="font-display font-bold text-neutral-900 w-16 text-end">${e.price}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(e)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-neutral-200 text-neutral-600"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(e.id)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-rose-100 text-rose-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {editing && <ExpertForm initial={editing} onSave={onSave} onCancel={() => setEditing(null)} />}
    </div>
  );
};

export const AdminSimpleList = ({ type }) => {
  const { lang, loc } = useLang();
  const [subs, setSubs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    if (type === "subscribers") api.listSubscribers().then(setSubs).catch(() => {});
    if (type === "bookings" || type === "subscriptions" || type === "requests") api.listBookings().then(setBookings).catch(() => {});
    if (type === "applications") api.listApplications().then(setApps).catch(() => {});
  }, [type]);

  if (type === "applications") {
    return (
      <div className="space-y-3">
        {apps.length === 0 && <p className="p-8 text-center text-neutral-400 bg-white rounded-3xl border border-neutral-100">Henüz başvuru yok. "/become-expert" sayfasından test edebilirsiniz.</p>}
        {apps.map((a, i) => (
          <div key={i} className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-neutral-900">{a.name} <span className="text-neutral-400 font-normal">· {a.email}</span></div>
                <div className="text-xs text-neutral-500 mt-0.5">{a.category} {a.expertise ? `· ${a.expertise}` : ""} {a.price ? `· $${a.price}` : ""}</div>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 shrink-0">{a.status || "new"}</span>
            </div>
            {a.message && <p className="mt-2 text-sm text-neutral-600">{a.message}</p>}
            {a.links && <a href={a.links} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-indigo-600 hover:underline break-all">{a.links}</a>}
            <div className="text-[11px] text-neutral-400 mt-2">{a.at ? new Date(a.at).toLocaleString() : ""}</div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "subscribers") {
    return (
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden">
        {subs.length === 0 && <p className="p-8 text-center text-neutral-400">Henüz abone yok. Ana sayfadaki bültene kayıt olarak test edebilirsiniz.</p>}
        <div className="divide-y divide-neutral-100">
          {subs.map((s, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="w-9 h-9 rounded-full bg-rose-100 flex items-center justify-center"><Mail className="w-4 h-4 text-rose-500" /></div>
              <span className="font-medium text-neutral-800">{s.email}</span>
              <span className="ms-auto text-xs text-neutral-400">{s.date ? new Date(s.date).toLocaleDateString() : ""}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "investors") {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {INVESTORS.map((inv) => (
          <div key={inv.id} className="bg-white rounded-2xl border border-neutral-100 p-5 shadow-sm">
            <div className="w-10 h-10 rounded-xl bg-neutral-900 flex items-center justify-center mb-3"><Building2 className="w-5 h-5 text-white" /></div>
            <div className="font-semibold text-neutral-900">{inv.name}</div>
            <div className="text-sm text-neutral-500">{loc(inv.role)}</div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "categories") {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        {CATEGORIES.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-neutral-100 p-5 shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: c.accent }}><LayoutGrid className="w-5 h-5 text-white" /></div>
            <div>
              <div className="font-semibold text-neutral-900">{translations[lang]?.categories?.[c.key]}</div>
              <div className="text-xs text-neutral-400">{c.id}</div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "requests") {
    const reqList = bookings.filter((b) => b.period === "gift" || b.period === "request");
    return (
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden divide-y divide-neutral-100">
        {reqList.length === 0 && <p className="p-8 text-center text-neutral-400">Henüz talep yok. Uzman sayfasındaki "Hediye et" veya "Zaman talep et" ile oluşur.</p>}
        {reqList.map((b, i) => {
          const isGift = b.period === "gift";
          return (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isGift ? "bg-rose-100" : "bg-indigo-100"}`}>
                {isGift ? <Gift className="w-4 h-4 text-rose-500" /> : <Clock className="w-4 h-4 text-indigo-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-800 truncate">{isGift ? "🎁 Hediye Talebi" : "⏰ Zaman Talebi"} · {b.expert}</div>
                <div className="text-xs text-neutral-400 truncate">{b.account || "-"}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${isGift ? "bg-rose-50 text-rose-600" : "bg-indigo-50 text-indigo-600"}`}>{isGift ? "Hediye" : "Zaman"}</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === "subscriptions") {
    const subsList = bookings.filter((b) => b.plan && b.period !== "gift" && b.period !== "request");
    const monthly = subsList.filter((b) => b.period !== "session").reduce((s, b) => s + (Number(b.price) || 0), 0);
    return (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center mb-2"><Repeat className="w-4 h-4 text-indigo-600" /></div>
            <div className="text-2xl font-display font-extrabold text-neutral-900">{subsList.length}</div>
            <div className="text-xs text-neutral-400">Toplam Abonelik / Paket</div>
          </div>
          <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center mb-2"><DollarSign className="w-4 h-4 text-emerald-600" /></div>
            <div className="text-2xl font-display font-extrabold text-neutral-900">${monthly.toLocaleString()}</div>
            <div className="text-xs text-neutral-400">Aylık Tekrar Eden Gelir (MRR)</div>
          </div>
          <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-5">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center mb-2"><Star className="w-4 h-4 text-amber-600" /></div>
            <div className="text-2xl font-display font-extrabold text-neutral-900">{subsList.filter((b) => b.status === "pending").length}</div>
            <div className="text-xs text-neutral-400">Onay Bekleyen</div>
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden divide-y divide-neutral-100">
          {subsList.length === 0 && <p className="p-8 text-center text-neutral-400">Henüz abonelik/paket satışı yok. Bir uzman sayfasından bir plan seçip onaylayarak test edebilirsiniz.</p>}
          {subsList.map((b, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center shrink-0"><Repeat className="w-4 h-4 text-indigo-500" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-800 truncate">{b.plan}</div>
                <div className="text-xs text-neutral-400">{b.expert} · {b.method || "-"}{b.account ? ` (${b.account})` : ""} · {b.at ? new Date(b.at).toLocaleDateString() : ""}</div>
              </div>
              <div className="text-end shrink-0">
                <div className="font-display font-bold text-neutral-900">${b.price}<span className="text-xs font-medium text-neutral-400">{b.period === "session" ? " / Görüşme" : " / Ay"}</span></div>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{b.status || "pending"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // bookings (from API)
  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden divide-y divide-neutral-100">
      {bookings.length === 0 && <p className="p-8 text-center text-neutral-400">Henüz randevu yok. Bir uzman sayfasından randevu oluşturarak test edebilirsiniz.</p>}
      {bookings.filter((b) => !b.plan).map((b, i) => (
        <div key={i} className="flex items-center gap-3 p-4">
          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center"><CalendarCheck className="w-4 h-4 text-indigo-500" /></div>
          <div className="flex-1">
            <div className="font-medium text-neutral-800">{b.expert} · {b.plan ? `${b.plan} (${b.period === "session" ? "Görüşme" : "Aylık"})` : `${b.dur}dk`} · ${b.price}</div>
            <div className="text-xs text-neutral-400">{b.date ? `${b.date} ${b.time || ""}` : "Abonelik/Paket"} · {b.method || "-"}{b.account ? ` (${b.account})` : ""}</div>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600">{b.status || "pending"}</span>
        </div>
      ))}
    </div>
  );
};

// ---------- Categories admin ----------
const catEmpty = () => ({ id: "cat" + Date.now(), color: "#1a1a2e", accent: "#6366f1", image: "", name: { tr: "", en: "", ru: "", ar: "" } });

const CategoryForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar shadow-2xl animate-scale-in">
        <div className="sticky top-0 bg-white/90 backdrop-blur border-b border-neutral-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">{initial.name?.tr ? "Kategori Düzenle" : "Kategori Ekle"}</h3>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <LocalizedField label="Kategori Adı (dile göre)" value={f.name} onChange={(v) => set("name", v)} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Arkaplan Rengi"><input type="color" className="w-full h-11 rounded-xl border border-neutral-200 bg-white" value={f.color || "#1a1a2e"} onChange={(e) => set("color", e.target.value)} /></Field>
            <Field label="Vurgu Rengi"><input type="color" className="w-full h-11 rounded-xl border border-neutral-200 bg-white" value={f.accent || "#6366f1"} onChange={(e) => set("accent", e.target.value)} /></Field>
          </div>
          <ImageUpload label="Kategori Görseli" value={f.image} onChange={(v) => set("image", v)} rounded="rounded-2xl" aspect="w-28 h-28" />
        </div>
        <div className="sticky bottom-0 bg-white border-t border-neutral-100 px-6 py-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} className="rounded-xl">İptal</Button>
          <Button onClick={() => onSave(f)} className="rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-6">Kaydet</Button>
        </div>
      </div>
    </div>
  );
};

export const AdminCategories = () => {
  const { loc } = useLang();
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  useEffect(() => { api.getCategories().then((r) => setList(r.length ? r : CATEGORIES)).catch(() => setList(CATEGORIES)); }, []);
  const onSave = async (f) => {
    const exists = list.find((x) => x.id === f.id);
    if (!exists) f.order = list.length;
    try {
      if (exists) await api.updateCategory(f.id, f); else await api.createCategory(f);
      setList((p) => (exists ? p.map((x) => (x.id === f.id ? f : x)) : [...p, f]));
      toast({ title: "Kaydedildi ✓" });
    } catch { toast({ title: "Kayıt başarısız (yetki?)" }); }
    setEditing(null);
  };
  const remove = async (id) => { try { await api.deleteCategory(id); setList((p) => p.filter((x) => x.id !== id)); toast({ title: "Silindi" }); } catch { toast({ title: "Silme başarısız" }); } };
  const onDrop = async (to) => {
    const from = dragIdx; setDragIdx(null);
    if (from === null || from === to) return;
    const arr = [...list]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    const withOrder = arr.map((c, i) => ({ ...c, order: i }));
    setList(withOrder);
    try { await Promise.all(withOrder.map((c) => api.updateCategory(c.id, c))); toast({ title: "Sıra kaydedildi ✓" }); } catch { toast({ title: "Sıra kaydedilemedi" }); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-neutral-400">Kartları sürükleyerek sırala.</p>
        <Button onClick={() => setEditing(catEmpty())} className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"><Plus className="w-4 h-4 me-1" /> Kategori Ekle</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {list.map((c, i) => (
          <div
            key={c.id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className={`bg-white rounded-2xl border p-5 shadow-sm flex items-center gap-3 transition ${dragIdx === i ? "opacity-50 border-indigo-400" : "border-neutral-100"}`}
          >
            <GripVertical className="w-5 h-5 text-neutral-300 cursor-move shrink-0" />
            <div className="w-14 h-14 rounded-xl overflow-hidden flex items-center justify-center shrink-0" style={{ background: c.accent || "#6366f1" }}>
              {mediaUrl(c.image) ? <img src={mediaUrl(c.image)} alt="" className="w-full h-full object-cover" /> : <LayoutGrid className="w-6 h-6 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-neutral-900 truncate">{(c.name && loc(c.name)) || c.key || c.id}</div>
              <div className="text-xs text-neutral-400">{c.id}</div>
            </div>
            <button onClick={() => setEditing(c)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-neutral-200 text-neutral-600"><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(c.id)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-rose-100 text-rose-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {editing && <CategoryForm initial={editing} onSave={onSave} onCancel={() => setEditing(null)} />}
    </div>
  );
};

// ---------- Investors admin ----------
const invEmpty = () => ({ id: "inv" + Date.now(), name: "", image: "", role: { tr: "", en: "", ru: "", ar: "" } });

const InvestorForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar shadow-2xl animate-scale-in">
        <div className="sticky top-0 bg-white/90 backdrop-blur border-b border-neutral-100 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="font-display font-bold text-lg">{initial.name ? "Yatırımcı Düzenle" : "Yatırımcı Ekle"}</h3>
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Ad"><input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <LocalizedField label="Rol / Ünvan (dile göre)" value={f.role} onChange={(v) => set("role", v)} />
          <ImageUpload label="Logo / Fotoğraf" value={f.image} onChange={(v) => set("image", v)} rounded="rounded-2xl" aspect="w-24 h-24" />
        </div>
        <div className="sticky bottom-0 bg-white border-t border-neutral-100 px-6 py-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={onCancel} className="rounded-xl">İptal</Button>
          <Button onClick={() => onSave(f)} className="rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-6">Kaydet</Button>
        </div>
      </div>
    </div>
  );
};

export const AdminInvestors = () => {
  const { loc } = useLang();
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  useEffect(() => { api.getInvestors().then((r) => setList(r.length ? r : INVESTORS)).catch(() => setList(INVESTORS)); }, []);
  const onSave = async (f) => {
    const exists = list.find((x) => x.id === f.id);
    try {
      if (exists) await api.updateInvestor(f.id, f); else await api.createInvestor(f);
      setList((p) => (exists ? p.map((x) => (x.id === f.id ? f : x)) : [...p, f]));
      toast({ title: "Kaydedildi ✓" });
    } catch { toast({ title: "Kayıt başarısız (yetki?)" }); }
    setEditing(null);
  };
  const remove = async (id) => { try { await api.deleteInvestor(id); setList((p) => p.filter((x) => x.id !== id)); toast({ title: "Silindi" }); } catch { toast({ title: "Silme başarısız" }); } };
  return (
    <div>
      <div className="flex justify-end mb-5">
        <Button onClick={() => setEditing(invEmpty())} className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"><Plus className="w-4 h-4 me-1" /> Yatırımcı Ekle</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((inv) => (
          <div key={inv.id} className="bg-white rounded-2xl border border-neutral-100 p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-neutral-900 overflow-hidden flex items-center justify-center shrink-0">
                {mediaUrl(inv.image) ? <img src={mediaUrl(inv.image)} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-neutral-900 truncate">{inv.name}</div>
                <div className="text-sm text-neutral-500 truncate">{loc(inv.role)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-3">
              <button onClick={() => setEditing(inv)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-neutral-200 text-neutral-600"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(inv.id)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-rose-100 text-rose-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <InvestorForm initial={editing} onSave={onSave} onCancel={() => setEditing(null)} />}
    </div>
  );
};

export const AdminContent = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState("tr");
  const DEFAULTS = {
    tr: { heroTitle: "Aradığın uzmanla birebir görüş", mission: "Herkese, herkese erişim" },
    en: { heroTitle: "Book a 1-on-1 with the expert you need", mission: "Give everyone access to anyone" },
    ru: { heroTitle: "Личная встреча с нужным экспертом", mission: "Доступ каждого к каждому" },
    ar: { heroTitle: "احجز جلسة خاصة مع الخبير الذي تحتاجه", mission: "امنح الجميع الوصول إلى أي شخص" },
  };
  const [data, setData] = useState(DEFAULTS);
  useEffect(() => {
    api.getSettings().then((s) => {
      if (s && s.content) {
        setData({
          tr: { ...DEFAULTS.tr, ...(s.content.tr || {}) },
          en: { ...DEFAULTS.en, ...(s.content.en || {}) },
          ru: { ...DEFAULTS.ru, ...(s.content.ru || {}) },
          ar: { ...DEFAULTS.ar, ...(s.content.ar || {}) },
        });
      }
    }).catch(() => {});
  }, []);
  const set = (k, v) => setData((p) => ({ ...p, [tab]: { ...p[tab], [k]: v } }));
  const save = async () => {
    try { await api.putSettings({ content: data }); toast({ title: "İçerik kaydedildi ✓" }); }
    catch { toast({ title: "Kayıt başarısız (yetki?)" }); }
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6 max-w-2xl">
      <div className="flex gap-2 mb-6">
        {["tr", "en", "ru", "ar"].map((l) => (
          <button key={l} onClick={() => setTab(l)} className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === l ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"}`}>{l.toUpperCase()}</button>
        ))}
      </div>
      <div className="space-y-4">
        <Field label="Hero Başlık"><input className={input} value={data[tab].heroTitle} onChange={(e) => set("heroTitle", e.target.value)} /></Field>
        <Field label="Misyon Başlık"><input className={input} value={data[tab].mission} onChange={(e) => set("mission", e.target.value)} /></Field>
      </div>
      <Button onClick={save} className="mt-6 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-6"><Check className="w-4 h-4 me-1" /> Kaydet</Button>
      <p className="mt-4 text-xs text-neutral-400">Ana sayfadaki hero ve misyon başlıklarını dile göre düzenle. Kalıcı olarak kaydedilir.</p>
    </div>
  );
};

export const AdminSettings = () => {
  const { toast } = useToast();
  const [s, setS] = useState(SITE_SETTINGS);
  useEffect(() => { api.getSettings().then((d) => { if (d && Object.keys(d).length) setS({ ...SITE_SETTINGS, ...d }); }).catch(() => {}); }, []);
  const save = async () => {
    try { await api.putSettings(s); toast({ title: "Ayarlar kaydedildi ✓" }); }
    catch (e) { toast({ title: "Kayıt başarısız (yetki?)" }); }
  };
  const modes = [{ id: "demo", label: "Demo", icon: Check }, { id: "payment", label: "Gerçek Ödeme (Stripe)", icon: Check }, { id: "video", label: "Video Görüşme", icon: Check }];

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg mb-4">Genel</h3>
        <Field label="Marka Adı"><input className={input} value={s.brandName} onChange={(e) => setS((p) => ({ ...p, brandName: e.target.value }))} /></Field>
      </div>
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg mb-1">Sonuçlar Galerisi</h3>
        <p className="text-sm text-neutral-500 mb-4">Ana sayfadaki "Aradığın sonuçları al" bölümünde gösterilen görseller.</p>
        <MultiImageUpload value={(s.results && s.results.length) ? s.results : RESULTS_IMAGES} onChange={(arr) => setS((p) => ({ ...p, results: arr }))} />
      </div>
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg mb-1">Randevu / Ödeme Modu</h3>
        <p className="text-sm text-neutral-500 mb-4">Hangi akışın aktif olacağını seç.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {modes.map((m) => (
            <button key={m.id} onClick={() => setS((p) => ({ ...p, activeBookingMode: m.id }))} className={`rounded-2xl border p-4 text-start transition ${s.activeBookingMode === m.id ? "border-indigo-600 bg-indigo-50" : "border-neutral-200 hover:border-neutral-400"}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${s.activeBookingMode === m.id ? "bg-indigo-600" : "bg-neutral-200"}`}>
                <Check className={`w-4 h-4 ${s.activeBookingMode === m.id ? "text-white" : "text-neutral-500"}`} />
              </div>
              <div className="font-semibold text-sm text-neutral-900">{m.label}</div>
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-neutral-400">Gerçek ödeme/video için ilgili API anahtarları backend aşamasında istenecek.</p>
      </div>
      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg mb-1">Ödeme Yöntemleri</h3>
        <p className="text-sm text-neutral-500 mb-4">Kullanıcıya rezervasyon ödemesinde gösterilecek aktif yöntemleri seç.</p>
        <div className="space-y-2">
          {PAYMENT_METHODS.map((m) => {
            const on = (s.payments || SITE_SETTINGS.payments)[m.id];
            return (
              <div key={m.id} className="flex items-center justify-between rounded-2xl border border-neutral-200 px-4 py-3">
                <span className="font-medium text-sm text-neutral-800">{m.label.tr}</span>
                <button
                  onClick={() => setS((p) => ({ ...p, payments: { ...(p.payments || SITE_SETTINGS.payments), [m.id]: !on } }))}
                  className={`relative w-12 h-7 rounded-full transition-colors ${on ? "bg-indigo-600" : "bg-neutral-300"}`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${on ? "start-6" : "start-1"}`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-neutral-400" /> Banka Hesapları (Havale/EFT)</h3>
          <Button
            onClick={() => setS((p) => ({ ...p, bankAccounts: [...(p.bankAccounts || SITE_SETTINGS.bankAccounts), { id: "b" + Date.now(), bank: "", holder: "", iban: "", currency: "TRY" }] }))}
            className="rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm h-9"
          ><Plus className="w-4 h-4 me-1" /> Hesap Ekle</Button>
        </div>
        <p className="text-sm text-neutral-500 mb-4">Havale/EFT seçen kullanıcıya gösterilecek aktif hesaplar.</p>
        <div className="space-y-3">
          {(s.bankAccounts || SITE_SETTINGS.bankAccounts).map((acc, idx) => {
            const upd = (k, v) => setS((p) => ({ ...p, bankAccounts: (p.bankAccounts || SITE_SETTINGS.bankAccounts).map((a, i) => i === idx ? { ...a, [k]: v } : a) }));
            const del = () => setS((p) => ({ ...p, bankAccounts: (p.bankAccounts || SITE_SETTINGS.bankAccounts).filter((_, i) => i !== idx) }));
            return (
              <div key={acc.id} className="rounded-2xl border border-neutral-200 p-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={input} placeholder="Banka Adı" value={acc.bank} onChange={(e) => upd("bank", e.target.value)} />
                  <input className={input} placeholder="Hesap Sahibi" value={acc.holder} onChange={(e) => upd("holder", e.target.value)} />
                </div>
                <div className="grid sm:grid-cols-3 gap-3 mt-3">
                  <input className={`${input} sm:col-span-2 font-mono`} placeholder="IBAN" value={acc.iban} onChange={(e) => upd("iban", e.target.value)} />
                  <select className={input} value={acc.currency} onChange={(e) => upd("currency", e.target.value)}>
                    {["TRY", "USD", "EUR", "GBP"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex justify-end mt-2">
                  <button onClick={del} className="inline-flex items-center gap-1 text-sm text-rose-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /> Sil</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm p-6">
        <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-1"><CreditCard className="w-5 h-5 text-neutral-400" /> Kart Ödemesi (Stripe)</h3>
        <p className="text-sm text-neutral-500 mb-4">Kredi/Banka kartı entegrasyonu. Gerçek çekim için Stripe anahtarı ve backend gerekir.</p>
        <Field label="Stripe Publishable Key (pk_...)">
          <input className={`${input} font-mono`} placeholder="pk_live_..." value={(s.cardConfig || SITE_SETTINGS.cardConfig).publishableKey}
            onChange={(e) => setS((p) => ({ ...p, cardConfig: { ...(p.cardConfig || SITE_SETTINGS.cardConfig), publishableKey: e.target.value } }))} />
        </Field>
        <p className="mt-2 text-xs text-amber-600 bg-amber-50 rounded-xl p-2.5">Not: Şu an kart formu demo. Gerçek ödeme için Stripe secret key + backend entegrasyonu gerekli.</p>
      </div>

      <Button onClick={save} className="rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white px-6"><Check className="w-4 h-4 me-1" /> Kaydet</Button>
    </div>
  );
};
